import { useMemo } from 'react'
import { TestingMethodType } from '@phrasee/phrasee-typings/Graphql/interfaces'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Avatar from 'common/components/Avatar'
import Badge from 'common/components/Badge'
import { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import TableCheckbox from 'common/components/table/Checkbox'
import { useAppSelector } from 'common/hooks/redux'
import useTableActions from 'common/hooks/useTableActions'
import { TextWithLineBreaks } from 'common/variantsUtils'
import ActionCell from 'features/unifiedFlow/contentPage/components/ActionsCell'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'

import { ResponseVariantExtended } from '../hooks/useManageOptimizeVariants'
import { VariantStatus } from '../utils/variantUtils'

export type UseManageTableColumnsProps = {
  selectedVariantIds: string[]
  variantsToBeApprovedFiltered: ResponseVariantExtended[]
  approvingVariantIds: string[]
  droppingVariantIds: string[]
  statusFilter: VariantStatus
  handleSelectAll: () => void
  handleRowClick: (variant: ResponseVariantExtended) => void
  handleCheckboxClick: (variant: ResponseVariantExtended) => void
  handleApproveVariant: (variant: ResponseVariantExtended) => void
  handleDropVariant: (variant: ResponseVariantExtended) => void
}

export const useManageTableColumns = ({
  selectedVariantIds,
  variantsToBeApprovedFiltered,
  approvingVariantIds,
  droppingVariantIds,
  statusFilter,
  handleSelectAll,
  handleCheckboxClick,
  handleApproveVariant,
  handleDropVariant,
}: UseManageTableColumnsProps): Column<ResponseVariantExtended>[] => {
  const flags = useFlags()
  const { onRequestChange } = useTableActions()
  const { data: selectedElement } = useSelectedComponentElement()
  const { content } = useContent()

  const isPersonalize =
    !!selectedElement?.personalization_id && !!content?.account_id
  const campaignData = useAppSelector(
    (state) => state.campaignStates.campaignData
  )
  const broadcastTypes: TestingMethodType[] = [
    'split_test_partial',
    'split_test_full',
  ]
  const isBroadcastCampaign = broadcastTypes.includes(
    campaignData?.campaign_configuration?.testing_method
      ?.type as TestingMethodType
  )

  return useMemo(() => {
    const isControlVariant = (variant: ResponseVariantExtended) => {
      if (isPersonalize) {
        return variant.isHoldout === true
      }
      return variant.ownsl === true
    }

    const columns: Column<ResponseVariantExtended>[] = []

    if (statusFilter !== 'dropped' && !isBroadcastCampaign) {
      columns.push({
        id: 'checkbox',
        accessor: (row: any) => row._id,
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        disableSortBy: true,
        disableGlobalFilter: true,
        disableFilters: true,
        Header: () => (
          <div className="flex items-center justify-center font-medium uppercase">
            <TableCheckbox
              checked={
                selectedVariantIds.length > 0 &&
                selectedVariantIds.length ===
                  variantsToBeApprovedFiltered.length
              }
              indeterminate={
                selectedVariantIds.length > 0 &&
                selectedVariantIds.length < variantsToBeApprovedFiltered.length
              }
              onChange={handleSelectAll}
              aria-label="Select all variants"
            />
          </div>
        ),
        Cell: ({ row }) => {
          const id = row.original._id
          const isControl = isControlVariant(row.original)
          const shouldHideCheckbox = statusFilter === 'live' && isControl

          if (shouldHideCheckbox) {
            return (
              <BaseCell
                style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                className="flex-shrink-0"
              >
                {null}
              </BaseCell>
            )
          }

          return (
            <BaseCell
              style={{ width: 40, minWidth: 40, maxWidth: 40 }}
              className="flex-shrink-0"
            >
              <TableCheckbox
                checked={selectedVariantIds.includes(id)}
                isDisabled={row.original.isLoading}
                onChange={(e) => {
                  e.stopPropagation()
                  if (!row.original.isLoading) {
                    handleCheckboxClick(row.original)
                  }
                }}
                aria-label={`Select variant ${row.index + 1}`}
              />
            </BaseCell>
          )
        },
      })
    }

    columns.push({
      Header: () => (
        <div className="flex items-center gap-2">
          <span>VARIANTS</span>
        </div>
      ),
      accessor: 'text',
      minWidth: 450,
      disableSortBy: true,
      Cell: ({ row, rows }) => {
        let isControl = false
        const original = row.original as ResponseVariantExtended
        if (isPersonalize) {
          isControl = original.isHoldout === true
        } else {
          isControl = original.ownsl === true
        }
        // Check if first row is control to adjust numbering
        const firstRowIsControl =
          rows.length > 0 &&
          isControlVariant(rows[0].original as ResponseVariantExtended)
        const displayNumber = firstRowIsControl ? row.index : row.index + 1

        return (
          <div className="flex items-center gap-3 py-2 pl-4 pr-0">
            {!isControl && (
              <span className="text-coolGray-500 font-medium flex-shrink-0">
                {displayNumber}.
              </span>
            )}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <TextWithLineBreaks text={row.original.text} />
              </div>
              <div className="flex items-center gap-2">
                {isControl && (
                  <Badge
                    size="small"
                    className="uppercase"
                    text="Control"
                    variant="control"
                  />
                )}
                {flags.jacquardGeneratedControl &&
                  row.original.reason_included === 'Control Line' && (
                    <Badge
                      size="small"
                      className="uppercase"
                      text="AI Control"
                      variant="control"
                    />
                  )}
                {statusFilter === 'live' &&
                  row.original.status === 'approved' && (
                    <Badge
                      size="small"
                      className="uppercase"
                      text="Pending live"
                      variant="neutral"
                    />
                  )}
              </div>
            </div>
          </div>
        )
      },
    })

    if (statusFilter === 'live' && !isPersonalize) {
      columns.push({
        Header: () => (
          <div className="flex items-center justify-center">
            <span>DATE APPROVED</span>
          </div>
        ),
        id: 'approved_date',
        accessor: '_id',
        minWidth: 180,
        disableSortBy: true,
        disableGlobalFilter: true,
        disableFilters: true,
        Cell: ({ row }) => {
          const statusUpdatedAt =
            row.original.bandit_status?.status_updated_at ||
            row.original.status_updated_at
          const approvedBy =
            row.original.bandit_status?.approved_by || row.original.approved_by
          const formattedDate = statusUpdatedAt
            ? new Date(statusUpdatedAt).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
              })
            : '-'
          const id = row.original._id
          const isControl = isControlVariant(row.original)
          return (
            <div className="flex items-center py-2 pl-4 pr-4 gap-3 group flex-nowrap">
              <span className="text-coolGray-700 whitespace-nowrap flex-shrink-0">
                {formattedDate}
              </span>
              {approvedBy?.fname && (
                <Avatar
                  firstName={approvedBy.fname}
                  lastName={approvedBy.lname}
                  showTooltip
                  fullName={`${approvedBy.fname} ${
                    approvedBy.lname || ''
                  }`.trim()}
                  className="w-6 h-6 text-xs flex-shrink-0"
                />
              )}
              {!isControl && !isBroadcastCampaign && (
                <div
                  className={`transition-opacity duration-500 ease-in-out ${
                    droppingVariantIds.includes(id)
                      ? 'visible opacity-100'
                      : 'invisible group-hover:visible group-hover:opacity-100 opacity-0'
                  }`}
                >
                  <ActionCell
                    isDropping={droppingVariantIds.includes(id)}
                    onDrop={() => handleDropVariant(row.original)}
                  />
                </div>
              )}
            </div>
          )
        },
      })
    } else if (
      statusFilter === 'pending' ||
      (statusFilter === 'live' && isPersonalize)
    ) {
      columns.push({
        id: 'actions',
        accessor: '_id',
        minWidth: 75,
        disableSortBy: true,
        disableGlobalFilter: true,
        disableFilters: true,
        align: 'right',
        Cell: ({ row }) => {
          const id = row.original._id
          const isControl = isControlVariant(row.original)
          const shouldHideActions = statusFilter === 'live' && isControl

          if (shouldHideActions) {
            return (
              <div className="flex items-center justify-end py-2 pl-4 pr-4 gap-3 group"></div>
            )
          }

          return (
            <div className="flex items-center justify-end py-2 pl-4 pr-4 gap-3 group">
              <div
                className={`transition-opacity duration-500 ease-in-out ${
                  row.original.isLoading || approvingVariantIds.includes(id)
                    ? 'visible opacity-100'
                    : 'invisible group-hover:visible group-hover:opacity-100 opacity-0'
                }`}
              >
                <ActionCell
                  isTweaking={
                    row.original.isLoading && !row.original.isRefreshed
                  }
                  isSuccessful={row.original.isRefreshed}
                  {...(statusFilter === 'pending'
                    ? {
                        onRequestChange: () => onRequestChange(row.original),
                        onApprove: () => handleApproveVariant(row.original),
                        isApproving: approvingVariantIds.includes(id),
                      }
                    : statusFilter === 'live'
                      ? {
                          onDrop: () => handleDropVariant(row.original),
                          isDropping: droppingVariantIds.includes(id),
                        }
                      : {})}
                />
              </div>
            </div>
          )
        },
      })
    }

    return columns
  }, [
    statusFilter,
    isBroadcastCampaign,
    isPersonalize,
    selectedVariantIds,
    variantsToBeApprovedFiltered.length,
    handleSelectAll,
    handleCheckboxClick,
    flags.jacquardGeneratedControl,
    droppingVariantIds,
    handleDropVariant,
    approvingVariantIds,
    onRequestChange,
    handleApproveVariant,
  ])
}
